import {Injectable} from "@angular/core";

export interface ExternalResourceOptions {
    integrity?: string;
    crossorigin?: string;
}

/**
 * Loads external scripts and stylesheets on demand, deduped by URL.
 * A failed load is evicted from the cache so a later call can retry.
 */
@Injectable({providedIn: "root"})
export class ExternalScriptLoaderService {
    private readonly scripts: Map<string, Promise<void>> = new Map<string, Promise<void>>();
    private readonly stylesheets: Map<string, Promise<void>> = new Map<string, Promise<void>>();

    public loadScript(url: string, options?: ExternalResourceOptions): Promise<void> {
        return this.load(this.scripts, url, () => {
            const script: HTMLScriptElement = document.createElement("script");
            script.src = url;
            script.async = false;
            this.applyOptions(script, options);
            return script;
        });
    }

    public loadStylesheet(url: string, options?: ExternalResourceOptions): Promise<void> {
        return this.load(this.stylesheets, url, () => {
            const link: HTMLLinkElement = document.createElement("link");
            link.rel = "stylesheet";
            link.href = url;
            this.applyOptions(link, options);
            return link;
        });
    }

    public isLoaded(url: string) {
        return this.scripts.has(url) || this.stylesheets.has(url) || this.alreadyInHead(url);
    }

    private load(cache: Map<string, Promise<void>>, url: string, createElement: () => HTMLScriptElement | HTMLLinkElement): Promise<void> {
        let load: Promise<void> = cache.get(url);
        if (!load) {
            load = this.alreadyInHead(url) ? Promise.resolve() : new Promise<void>((resolve: () => void, reject: (reason: Error) => void) => {
                const element: HTMLScriptElement | HTMLLinkElement = createElement();
                element.onload = () => resolve();
                element.onerror = () => reject(new Error(`Failed to load ${url}`));
                document.head.appendChild(element);
            }).catch((error: Error) => {
                cache.delete(url);
                throw error;
            });
            cache.set(url, load);
        }
        return load;
    }

    /**
     * True if a <script>/<link> for this exact URL is already statically present in <head> - e.g. authored
     * directly in index.html rather than injected by this service. Lets a static tag take precedence
     * without double-loading, while still falling back to the on-demand path the moment that tag is
     * removed again.
     *
     * Presence is treated as already-loaded, which isn't strictly guaranteed if the tag is async/defer -
     * but callers of loadScript/loadStylesheet only ever run well after such a tag would have started
     * downloading, and a too-early miss fails gracefully (the caller's own error handling) rather than
     * breaking anything, so this doesn't require the static tag to be a blocking one.
     */
    private alreadyInHead(url: string): boolean {
        const absoluteUrl: string = new URL(url, document.baseURI).href;
        return Array.from(document.head.children).some((element: Element) =>
            (element as HTMLScriptElement).src === absoluteUrl || (element as HTMLLinkElement).href === absoluteUrl);
    }

    private applyOptions(element: HTMLScriptElement | HTMLLinkElement, options?: ExternalResourceOptions): void {
        if (options?.integrity) {
            element.integrity = options.integrity;
        }
        if (options?.crossorigin) {
            element.crossOrigin = options.crossorigin;
        }
    }
}
